import React, { useState, useEffect } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import loyaltyCommissionService from '../../../services/LoyaltyCommissionService';
import { UserModel } from '../../../models/UserModel';
import { LogoType } from '../../../static/types/commonTypes/logoType';
import PromoCodeModal from '../codePromo/CodePromoModal';
import Swal from 'sweetalert2';
import { EmailAutorized, updateUserInfo, updateUserInfoWithFiles } from '../../../services/User_service';
import { isValidRcs } from '../../../utils/functions/validateRCSNumber';
import { fetchCompanyDetailsByRcs } from '../../../utils/functions/fetchCompanyDetailsByRcs';
import UserLimitCards from '../UserLimitCard';
import { updateUserLimits, resetUserLimits } from '../../../services/User_service';
import { toast } from 'react-toastify';
import RibModal from '../../client/avoir/RibModal';
import { deleteLogo, getAllLogosByUser, notifyLogoDeletion } from '../../../services/Logo_service';
import discountLimitsService from '../../../services/discountLimitsService';
import PersonalInfoTab from './Personalinfotab';
import UserHeader from './Userheader';
import ConnectionInfoTab from './Connectioninfotab';
import ProfessionalDocumentsTab from './Professionaldocumentstab ';
import CommissionSection from './Commissionsection';
import ActionButtons from './Actionbuttons';
import CommissionModal from './Commissionmodal';
import LogosSection from './Logossection';
import LogoDeleteModal from './Logodeletemodal';
import '../../../css/DetailUser.css';
import '../../../css/customButtons.css';


type CommissionType = {
  id: number;
  commissionPercentage: number;
  isActive: boolean;
  description?: string;
  validFrom?: string;
  validUntil?: string;
};

interface DetailUserProps {
  user: UserModel;
  logos: LogoType[];
  remiseNow: number;
  setRemiseNow: (remise: number) => void;
  saveRemiseChange: () => void;
  onLogosUpdate?: () => void;
  onUserUpdate?: (updatedUser: UserModel) => void;
}

const DetailUser: React.FC<DetailUserProps> = ({
  user,
  logos: initialLogos,
  remiseNow,
  setRemiseNow,
  saveRemiseChange,
  onLogosUpdate,
  onUserUpdate,
}) => {
  const [logos, setLogos] = useState<LogoType[]>(initialLogos);
  const primaryColor = '#3FA1B3';

  // Commission state
  const [commission, setCommission] = useState<CommissionType | null>(null);
  const [loadingComm, setLoadingComm] = useState<boolean>(true);

  // Modal state
  const [showModal, setShowModal] = useState<boolean>(false);
  const [commissionPct, setCommissionPct] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [validFrom, setValidFrom] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Promo modal
  const [showPromoModal, setShowPromoModal] = useState<boolean>(false);

  // Email loading state
  const [emailLoading, setEmailLoading] = useState<boolean>(false);

  // Editing user state
  const [localUser, setLocalUser] = useState<UserModel>(user);
  const [editingMode, setEditingMode] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Professional documents state (admin uploads)
  const [profDocs, setProfDocs] = useState<{
    kbisFile: File | null;
    identityRecto: File | null;
    identityVerso: File | null;
    identityType: 'cin' | 'passeport';
    ribFile: File | null;
  }>({
    kbisFile: null,
    identityRecto: null,
    identityVerso: null,
    identityType: 'cin',
    ribFile: null
  });

  // RIB modal & values
  const [showRibModal, setShowRibModal] = useState<boolean>(false);
  const [ribValues, setRibValues] = useState<{ rib?: string; bic?: string }>({});

  // upload progress
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // RCS validation state
  const [isValidatingRcs, setIsValidatingRcs] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const [showPlafondModal, setShowPlafondModal] = useState(false);

  const [selectedLogos, setSelectedLogos] = useState<number[]>([]);
  const [showDeleteLogoModal, setShowDeleteLogoModal] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [isDeletingLogos, setIsDeletingLogos] = useState(false);

  // État pour l'édition de la limite de remise
  const [editingDiscountLimit, setEditingDiscountLimit] = useState(false);
  const [tempDiscountLimit, setTempDiscountLimit] = useState<number>(0);
  const [savingDiscountLimit, setSavingDiscountLimit] = useState(false);

  const refreshLogos = async () => {
    try {
      const userLogos = await getAllLogosByUser(user.id.toString());
      setLogos(userLogos || []);
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement des logos:', error);
      setLogos([]);
    }
  };

  // Sync prop -> local
  useEffect(() => {
    setLocalUser(user);
    setProfDocs({
      kbisFile: null,
      identityRecto: null,
      identityVerso: null,
      ribFile: null,
      identityType: (user.identity_document_type as any) || 'cin',
    });
    setRibValues({
      rib: (user as any).rib || (user as any).account_number || undefined,
      bic: (user as any).bic || (user as any).bic_swift || undefined,
    });

    setTempDiscountLimit((user as any).discount_limit_percentage || 0);
    setEditingMode(false);
  }, [user, user.id, user.user_type]);

  const handleSavePlafond = async (id: number, values: any) => {
    await updateUserLimits(id, values);
    toast.success("Plafond mis à jour !");
  };

  const handleResetPlafond = async (id: number) => {
    await resetUserLimits(id);
    toast.success("Plafond réinitialisé !");
  };

  // Fetch commission on mount
  useEffect(() => {
    (async () => {
      try {
        setLoadingComm(true);
        const res = await loyaltyCommissionService.getUserCommission(user.id);
        if (res.success && res.commission) {
          const c = res.commission as any;
          setCommission({
            id: c.id,
            commissionPercentage: Number(c.commissionPercentage),
            isActive: c.isActive,
            description: c.description,
            validFrom: c.validFrom,
            validUntil: c.validUntil,
          });
        } else {
          setCommission(null);
        }
      } catch (err) {
        console.error('Erreur getUserCommission:', err);
      } finally {
        setLoadingComm(false);
      }
    })();
  }, [user.id]);

  useEffect(() => {
    refreshLogos();
  }, [user.id]);

  useEffect(() => {
    const handleLogosUpdated = (event: any) => {
      const { userId, deletedLogoIds } = event.detail || {};

      if (userId === user.id) {
        refreshLogos();
      }

      if (deletedLogoIds && Array.isArray(deletedLogoIds)) {
        setLogos(prev => prev.filter(logo => !deletedLogoIds.includes(logo.id)));
      }
    };

    window.addEventListener('logosUpdated', handleLogosUpdated as EventListener);
    window.addEventListener('userUpdated', handleLogosUpdated as EventListener);

    return () => {
      window.removeEventListener('logosUpdated', handleLogosUpdated as EventListener);
      window.removeEventListener('userUpdated', handleLogosUpdated as EventListener);
    };
  }, [user.id]);

  useEffect(() => {
    setLogos(initialLogos);
  }, [initialLogos]);

  // Toggle commission active/inactive
  const handleToggleCommission = async () => {
    if (!commission) return;
    try {
      await loyaltyCommissionService.toggleUserCommissionStatus(user.id, commission.id);
      const updated = await loyaltyCommissionService.getUserCommission(user.id);
      if (updated.success && updated.commission) {
        const c = updated.commission as any;
        setCommission({
          id: c.id,
          commissionPercentage: Number(c.commissionPercentage),
          isActive: c.isActive,
          description: c.description,
          validFrom: c.validFrom,
          validUntil: c.validUntil,
        });
      }
    } catch (err) {
      console.error('Erreur toggleCommission:', err);
    }
  };

  const handleEmailAutorisation = async () => {
    setEmailLoading(true);
    try {
      const response = await EmailAutorized(user.id);

      if (response.email_autorized === false) {
        toast.success(
          "L'envoi d'emails pour cet utilisateur a été désactivé avec succès."
        );
      } else {
        toast.success(
          "L'envoi d'emails pour cet utilisateur a été activé avec succès."
        );
      }
    } catch (error) {
      toast.error(
        "Erreur lors de la mise à jour de l'autorisation email."
      );
      console.error(
        "Erreur lors de la mise à jour de l'autorisation email:",
        error
      );
    } finally {
      setEmailLoading(false);
    }
  };

  // Open commission modal
  const openModal = () => {
    setCommissionPct(commission?.commissionPercentage ?? 0);
    setDescription(commission?.description ?? '');
    setValidFrom(commission?.validFrom ? commission.validFrom.split("T")[0] : '');
    setValidUntil(commission?.validUntil ? commission.validUntil.split("T")[0] : '');
    setShowModal(true);
  };

  // Save commission
  const handleSave = async () => {
    setSubmitting(true);
    try {
      await loyaltyCommissionService.createOrUpdateUserCommission(user.id, {
        commissionPercentage: commissionPct,
        description: description.trim() || undefined,
        validFrom: validFrom || undefined,
        validUntil: validUntil || undefined,
      });
      const updated = await loyaltyCommissionService.getUserCommission(user.id);
      if (updated.success && updated.commission) {
        const c = updated.commission as any;
        setCommission({
          id: c.id,
          commissionPercentage: Number(c.commissionPercentage),
          isActive: c.isActive,
          description: c.description,
          validFrom: c.validFrom,
          validUntil: c.validUntil,
        });
      }
      setShowModal(false);
    } catch (err) {
      console.error('Erreur saveCommission:', err);
      Swal.fire('Erreur', 'Impossible d\'enregistrer la commission', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const editableFields = [
    'name', 'firstname', 'adresse', 'number', 'country', 'postal_code',
    'raison_social', 'code_SIREN', 'code_NAF', 'tv_intra', 'civility', 'user_type', 'is_entreprise'
  ];

  const handleChangeLocal = (field: string, value: any) => {
    setLocalUser(prev => {
      const updated = { ...prev, [field]: value };

      if (field === 'user_type') {
        if (value === 'particulier') {
          updated.code_NAF = '';
          updated.tv_intra = '';
          updated.code_SIREN = '';
          updated.raison_social = '';
          updated.is_entreprise = false;
        }
        else if (value === 'entreprise') {
          updated.is_entreprise = true;
        }
        else if (value === 'professionnel') {
          updated.is_entreprise = true;
        }
      }

      return updated;
    });
  };

  const handleRcsChange = async (rcsValue: string) => {
    handleChangeLocal('code_SIREN', rcsValue);

    if (isValidRcs(rcsValue)) {
      setIsValidatingRcs(true);
      try {
        const companyDetails = await fetchCompanyDetailsByRcs(rcsValue);
        if (companyDetails && companyDetails.companyName) {
          handleChangeLocal('raison_social', companyDetails.companyName);

          if (companyDetails.address) {
            handleChangeLocal('adresse', companyDetails.address);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails de l\'entreprise:', error);
      } finally {
        setIsValidatingRcs(false);
      }
    }
  };

  const getDirtyFields = (original: UserModel, modified: UserModel) => {
    const changed: Record<string, any> = {};

    editableFields.forEach(f => {
      const orig = (original as any)[f];
      const mod = (modified as any)[f];

      if ((orig ?? '') !== (mod ?? '')) {
        changed[f] = mod;
      }
    });

    if (changed.user_type) {
      const isEntreprise =
        modified.user_type === 'entreprise' ||
        modified.user_type === 'professionnel';

      changed.is_entreprise = isEntreprise;
    }

    return changed;
  };

  const broadcastUserUpdate = (updatedUser: any) => {
    try {
      window.dispatchEvent(new CustomEvent("userUpdated", { detail: updatedUser }));
    } catch (e) {
      // noop
    }
  };

  const validateBusinessData = () => {
    if (localUser.user_type === 'entreprise' || localUser.user_type === 'professionnel') {
      const codesSiren = (localUser as any).code_SIREN;
      const raisonSocial = (localUser as any).raison_social;

      if (!codesSiren || !raisonSocial) {
        return {
          isValid: false,
          message: 'Pour un compte entreprise/professionnel, le code SIREN et la raison sociale sont obligatoires.'
        };
      }

      if (!isValidRcs(codesSiren)) {
        return {
          isValid: false,
          message: 'Le code SIREN doit contenir exactement 9 chiffres.'
        };
      }
    }

    return { isValid: true };
  };

  const handleSaveAll = async () => {
    if (!localUser.id) return;

    const validation = validateBusinessData();
    if (!validation.isValid) {
      Swal.fire('Validation échouée', validation.message, 'warning');
      return;
    }

    if (localUser.user_type === 'professionnel') {
      const hasExistingKbis = !!(localUser as any).kbis_document;
      const hasExistingRecto = !!(localUser as any).identity_document_recto;
      const identityTypeFromLocal = profDocs.identityType || (localUser.identity_document_type as any) || 'cin';
      const requiresVerso = identityTypeFromLocal === 'cin';
      const hasExistingVerso = !!(localUser as any).identity_document_verso;

      const kbisProvided = profDocs.kbisFile || hasExistingKbis;
      const rectoProvided = profDocs.identityRecto || hasExistingRecto;
      const versoProvided = (identityTypeFromLocal === 'passeport') ? true : (profDocs.identityVerso || hasExistingVerso);

      if (!kbisProvided || !rectoProvided || !versoProvided) {
        Swal.fire(
          'Validation échouée',
          'Pour un compte professionnel, le Kbis et la pièce d\'identité (recto et verso si CNI) sont obligatoires.',
          'warning'
        );
        return;
      }

      const hasExistingRibDoc = !!(localUser as any).rib_document;
      const hasNewRibDoc = !!profDocs.ribFile;
      const hasRibCoordinates = !!(ribValues.rib || (localUser as any).rib || (localUser as any).account_number);

      if (!hasExistingRibDoc && !hasNewRibDoc && !hasRibCoordinates) {
        Swal.fire(
          'Validation échouée',
          'Veuillez fournir le RIB du professionnel (document ou coordonnées bancaires via le modal).',
          'warning'
        );
        return;
      }
    }

    setSaving(true);
    setUploading(false);
    setUploadProgress(0);

    const changes = getDirtyFields(user, localUser);

    if (localUser.user_type === 'professionnel') {
      changes['documents_verified'] = true;
      changes['documents_verification_date'] = new Date().toISOString();
      if (ribValues.rib) changes['rib'] = ribValues.rib;
      if (ribValues.bic) changes['bic'] = ribValues.bic;
    }

    const hasFileUploads = !!(
      profDocs.kbisFile ||
      profDocs.identityRecto ||
      profDocs.identityVerso ||
      profDocs.ribFile
    );

    if (Object.keys(changes).length === 0 && !hasFileUploads) {
      setEditingMode(false);
      setSaving(false);
      Swal.fire('Aucune modification', 'Aucune donnée modifiée.', 'info');
      return;
    }

    try {
      let response;

      if (hasFileUploads) {
        setUploading(true);
        response = await updateUserInfoWithFiles(
          Number(localUser.id),
          changes,
          {
            kbisFile: profDocs.kbisFile || undefined,
            identityRecto: profDocs.identityRecto || undefined,
            identityVerso: profDocs.identityVerso || undefined,
            identityType: profDocs.identityType,
            ribFile: profDocs.ribFile || undefined,
          },
          (progressEvent) => {
            try {
              const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
              setUploadProgress(pct);
            } catch (e) {
              // ignore
            }
          }
        );
        setUploading(false);
      } else {
        response = await updateUserInfo(Number(localUser.id), changes);
      }

      const updated = response?.data?.data || response?.data || response;

      let updatedUser = { ...localUser } as any;

      if (updated) {
        updatedUser = { ...updatedUser, ...updated };

        if (hasFileUploads) {
          if (!updated.kbis_document && profDocs.kbisFile) {
            updatedUser.kbis_document = URL.createObjectURL(profDocs.kbisFile);
          }
          if (!updated.identity_document_recto && profDocs.identityRecto) {
            updatedUser.identity_document_recto = URL.createObjectURL(profDocs.identityRecto);
          }
          if (!updated.identity_document_verso && profDocs.identityVerso) {
            updatedUser.identity_document_verso = URL.createObjectURL(profDocs.identityVerso);
          }
          if (!updated.rib_document && profDocs.ribFile) {
            updatedUser.rib_document = URL.createObjectURL(profDocs.ribFile);
          }
        }
      } else {
        updatedUser = { ...updatedUser, ...changes };

        if (hasFileUploads) {
          if (profDocs.kbisFile) {
            updatedUser.kbis_document = URL.createObjectURL(profDocs.kbisFile);
          }
          if (profDocs.identityRecto) {
            updatedUser.identity_document_recto = URL.createObjectURL(profDocs.identityRecto);
          }
          if (profDocs.identityVerso) {
            updatedUser.identity_document_verso = URL.createObjectURL(profDocs.identityVerso);
          }
          if (profDocs.ribFile) {
            updatedUser.rib_document = URL.createObjectURL(profDocs.ribFile);
          }
        }
      }

      if (profDocs.identityType) {
        updatedUser.identity_document_type = profDocs.identityType;
      }

      if (localUser.user_type === 'professionnel') {
        updatedUser.documents_verified = true;
        updatedUser.documents_verification_date = new Date().toISOString();
      }

      if (ribValues.rib) {
        updatedUser.rib = ribValues.rib;
        updatedUser.account_number = ribValues.rib;
      }
      if (ribValues.bic) {
        updatedUser.bic = ribValues.bic;
        updatedUser.bic_swift = ribValues.bic;
      }

      setLocalUser(updatedUser);

      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }

      broadcastUserUpdate(updatedUser);

      setEditingMode(false);

      setProfDocs({
        kbisFile: null,
        identityRecto: null,
        identityVerso: null,
        ribFile: null,
        identityType: updatedUser.identity_document_type || 'cin',
      });

      setRibValues({
        rib: updatedUser.rib || updatedUser.account_number || '',
        bic: updatedUser.bic || updatedUser.bic_swift || '',
      });

      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Informations utilisateur mises à jour avec succès.',
        confirmButtonColor: '#2A919D',
        timer: 2000
      });

    } catch (err: any) {
      console.error('❌ Erreur updateUserInfo:', err);

      setLocalUser(user);

      const errorMessage = err?.response?.data?.message
        || err?.message
        || 'Erreur lors de la mise à jour';

      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
    } finally {
      setSaving(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancelEdit = () => {
    setLocalUser(user);
    setEditingMode(false);
    setEditingDiscountLimit(false);
    setTempDiscountLimit((user as any).discount_limit_percentage || 0);
    setProfDocs({
      kbisFile: null,
      identityRecto: null,
      identityVerso: null,
      ribFile: null,
      identityType: (user.identity_document_type as any) || 'cin',
    });
    setRibValues({
      rib: (user as any).rib || undefined,
      bic: (user as any).bic || undefined,
    });
    setUploading(false);
    setUploadProgress(0);
  };

  const formatRIB = (rib?: string | null) => {
    if (!rib) return '';
    const clean = rib.replace(/\s/g, '');
    if (clean.length !== 23) return rib;
    const bank = clean.slice(0, 5);
    const guichet = clean.slice(5, 10);
    const compte = clean.slice(10, 21);
    const cle = clean.slice(21);
    return `${bank} ${guichet} ${compte} ${cle}`;
  };

  const getDisplayedRib = () => {
    return ribValues.rib
      || (localUser as any).rib
      || (localUser as any).account_number
      || (localUser as any).accountNumber
      || '';
  };

  const getDisplayedBic = () => {
    return ribValues.bic
      || (localUser as any).bic
      || (localUser as any).bic_swift
      || (localUser as any).bicSwift
      || '';
  };

  const handleLogoSelection = (logoId: number) => {
    setSelectedLogos(prev =>
      prev.includes(logoId)
        ? prev.filter(id => id !== logoId)
        : [...prev, logoId]
    );
  };

  const handleSelectAllLogos = () => {
    if (selectedLogos.length === logos.length) {
      setSelectedLogos([]);
    } else {
      setSelectedLogos(logos.map(logo => logo.id));
    }
  };

  const openDeleteLogoModal = () => {
    if (selectedLogos.length === 0) {
      Swal.fire('Attention', 'Veuillez sélectionner au moins un logo', 'warning');
      return;
    }
    setShowDeleteLogoModal(true);
  };

  const handleDeleteLogos = async () => {
    if (!deletionReason.trim()) {
      Swal.fire('Attention', 'Veuillez fournir un motif de suppression', 'warning');
      return;
    }

    try {
      setIsDeletingLogos(true);

      for (const logoId of selectedLogos) {
        await deleteLogo(logoId);
      }

      await notifyLogoDeletion(
        user.email,
        user.firstname || user.name,
        selectedLogos.length,
        deletionReason.trim()
      );

      Swal.fire({
        icon: 'success',
        title: 'Suppression réussie',
        text: `${selectedLogos.length} logo(s) supprimé(s). Un email a été envoyé à l'utilisateur.`,
        confirmButtonColor: '#2A919D'
      });

      setLogos(prev => prev.filter(logo => !selectedLogos.includes(logo.id)));
      setSelectedLogos([]);
      setDeletionReason('');
      setShowDeleteLogoModal(false);

      if (onLogosUpdate) {
        await onLogosUpdate();
      }

      window.dispatchEvent(new CustomEvent("logosUpdated", {
        detail: {
          userId: user.id,
          deletedLogoIds: selectedLogos
        }
      }));

    } catch (error: any) {
      console.error('Erreur suppression logos:', error);
      Swal.fire('Erreur', error?.response?.data?.message || 'Impossible de supprimer les logos', 'error');
    } finally {
      setIsDeletingLogos(false);
    }
  };

  const handleSaveDiscountLimit = async () => {
    setSavingDiscountLimit(true);
    try {
      const result = await discountLimitsService.updateUserLimit({
        userId: localUser.id,
        percentage: tempDiscountLimit,
        active: tempDiscountLimit > 0,
      });

      if (result.success) {
        const updatedUser = {
          ...localUser,
          discount_limit_percentage: tempDiscountLimit,
          discount_limit_active: tempDiscountLimit > 0,
          discount_limit_updated_at: new Date().toISOString(),
        } as UserModel;

        setLocalUser(updatedUser);
        setEditingDiscountLimit(false);

        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }

        toast.success('Limite de remise mise à jour avec succès');
      } else {
        toast.error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (error: any) {
      console.error('Erreur mise à jour limite:', error);
      toast.error('Erreur lors de la mise à jour de la limite');
    } finally {
      setSavingDiscountLimit(false);
    }
  };

  return (
    <>
      {/* Contenu scrollable */}
      <div className="detail-user-wrapper" style={{ maxHeight: "100%", overflowY: "auto" }}>

        {/* Header avec photo de profil */}
        <UserHeader localUser={localUser} primaryColor={primaryColor} />

        {/* Tabs des informations */}
        <div>
          <Tabs
            id="user-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'info')}
            className="mb-3 mt-3"
          >
            {/* Onglet Infos perso */}
            <Tab eventKey="info" title="Information personnelle">
              <PersonalInfoTab
                localUser={localUser}
                editingMode={editingMode}
                saving={saving}
                isValidatingRcs={isValidatingRcs}
                uploading={uploading}
                primaryColor={primaryColor}
                profDocs={profDocs}
                ribValues={ribValues}
                editingDiscountLimit={editingDiscountLimit}
                tempDiscountLimit={tempDiscountLimit}
                savingDiscountLimit={savingDiscountLimit}
                user={user}
                onChangeLocal={handleChangeLocal}
                onRcsChange={handleRcsChange}
                onEditModeToggle={() => setEditingMode(true)}
                onSaveAll={handleSaveAll}
                onCancelEdit={handleCancelEdit}
                onDocumentsChange={(docs) => setProfDocs(docs)}
                onShowRibModal={() => setShowRibModal(true)}
                onEditDiscountLimit={setEditingDiscountLimit}
                onSaveDiscountLimit={handleSaveDiscountLimit}
                onDiscountLimitChange={setTempDiscountLimit}
              />
            </Tab>

            {/* Onglet Infos de connexion */}
            <Tab eventKey="connex" title="Information de connexion">
              <ConnectionInfoTab localUser={localUser} />
            </Tab>

            {/* Onglet Documents professionnels */}
            {localUser.user_type === 'professionnel' && (
              <Tab eventKey="documents" title="Documents professionnels">
                <ProfessionalDocumentsTab
                  localUser={localUser}
                  primaryColor={primaryColor}
                  onShowRibModal={() => setShowRibModal(true)}
                  getDisplayedRib={getDisplayedRib}
                  getDisplayedBic={getDisplayedBic}
                  formatRIB={formatRIB}
                />
              </Tab>
            )}
          </Tabs>
        </div>

        {/* Section Commission */}
        <CommissionSection
          commission={commission}
          primaryColor={primaryColor}
        />

        {/* Boutons d'action */}
        <ActionButtons
          localUser={localUser}
          commission={commission}
          loadingComm={loadingComm}
          emailLoading={emailLoading}
          onShowPromoModal={() => setShowPromoModal(true)}
          onEmailAutorisation={handleEmailAutorisation}
          onToggleCommission={handleToggleCommission}
          onOpenCommissionModal={openModal}
          onShowPlafondModal={() => setShowPlafondModal(true)}
        />

        {/* Section Logos */}
        <LogosSection
          logos={logos}
          selectedLogos={selectedLogos}
          primaryColor={primaryColor}
          onLogoSelection={handleLogoSelection}
          onSelectAllLogos={handleSelectAllLogos}
          onOpenDeleteModal={openDeleteLogoModal}
        />

      </div>

      {/* ✅ Modals EN DEHORS du wrapper overflow pour éviter le piège de z-index */}

      {/* Modal éditer commission */}
      <CommissionModal
        show={showModal}
        localUser={localUser}
        commissionPct={commissionPct}
        description={description}
        validFrom={validFrom}
        validUntil={validUntil}
        submitting={submitting}
        onHide={() => setShowModal(false)}
        onCommissionPctChange={setCommissionPct}
        onDescriptionChange={setDescription}
        onValidFromChange={setValidFrom}
        onValidUntilChange={setValidUntil}
        onSave={handleSave}
      />

      {/* Modal suppression logos */}
      <LogoDeleteModal
        show={showDeleteLogoModal}
        selectedLogos={selectedLogos}
        logos={logos}
        deletionReason={deletionReason}
        isDeletingLogos={isDeletingLogos}
        primaryColor={primaryColor}
        onHide={() => setShowDeleteLogoModal(false)}
        onDeletionReasonChange={setDeletionReason}
        onConfirmDelete={handleDeleteLogos}
      />

      {/* Modal code promo */}
      <PromoCodeModal
        show={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        user={localUser}
        onCreated={() => {
          Swal.fire('Succès', 'Code promo créé et envoyé', 'success');
        }}
      />

      {/* Modal plafond */}
      <UserLimitCards
        isOpen={showPlafondModal}
        onClose={() => setShowPlafondModal(false)}
        user={localUser}
        onSave={handleSavePlafond}
        onReset={handleResetPlafond}
      />

      {/* Modal RIB */}
      <RibModal
        visible={showRibModal}
        onCancel={() => setShowRibModal(false)}
        onConfirm={async (rib, bic) => {
          setRibValues({ rib, bic });
          setShowRibModal(false);
          Swal.fire('OK', 'Coordonnées bancaires enregistrées temporairement. Pensez à sauvegarder les modifications.', 'success');
        }}
        requireBic={true}
      />
    </>
  );
};

export default DetailUser;